import { EventInfoData } from '@shared/utils/eventInfo';
import { PageJson } from '../routes/CardCustomizer/CardCustomizer.types';
import { TextLayerData } from '../routes/CardCustomizer/components/Layer.types';
import { getOclock, getTimeOfDay, getTwoDigitsCardinalFromYear, NumeralsTranslations } from './dateFormatting';
import { mapRecord } from './mapRecord';
import { toTitleCase } from './toTitleCase';
import { PrimaryEventLocation } from '../routes/Dashboard/components/ProductDetailsPage/usePrimaryEventLocation';
import { utcToZonedTime } from 'date-fns-tz';

export type Time = { milliseconds: number; timezone?: string };

// Templates generated from the legacy theme system are already tagged.
// Each layer.name is mapped to one of values below.
// Rather than add a layer.pattern, we dynamically/virtually generate it.
const THEME_SYSTEM_LAYER_NAME_TO_PATTERN: Record<string, string> = {
  NAME1: '‹Firstname1›',
  NAME2: '‹Firstname2›',
  DATE: '‹WEEKDAY›, ‹MONTH› ‹D›, ‹YYYY›',
  VENUE: '‹VENUE›',
  TIME: '‹oclock› ‹timeofday›',
  LOCATION: '‹CityST›'
};

function fillPattern(pattern: string, data: Record<string, string | undefined>): string {
  // Trim in case any field is empty.
  return pattern.replace(/‹(\w+)›/g, (match, key) => data[key] ?? match).trim();
}

export function fillPageWithEventInfo(page: PageJson, eventInfo: EventInfoData | undefined, location: PrimaryEventLocation, tNumerals: NumeralsTranslations): string[] {
  // `eventSchedule` undefined could mean either loading or does not exist.
  if (!eventInfo) return [];

  // prettier-ignore
  const date = utcToZonedTime(location.utcMilliseconds, location.timezone ?? '');

  const Firstname1 = eventInfo.ownerFirstName ?? '';
  const Firstname2 = eventInfo.fianceeFirstName ?? '';
  const h12 = date ? date.getHours() % 12 : undefined;

  const { Venue, Address, City, CityState, CityST } = location;

  const Alphabetic: Record<string, string> = {
    // Names
    F1: Firstname1 ? Firstname1[0] : '',
    F2: Firstname2 ? Firstname2[0] : '',
    Firstname1,
    Firstname2,
    Middlename1: '',
    Middlename2: '',
    Lastname1: eventInfo.ownerLastName ?? '',
    Lastname2: eventInfo.fianceeLastName ?? '',

    // Date
    Weekday: date?.toLocaleDateString('en-US', { weekday: 'long' }) ?? '',
    Day: tNumerals.ordinals[date?.getDate() ?? 0]?.() ?? '',
    Dth: tNumerals.numericOrdinals[date?.getDate() ?? 0]?.() ?? '',
    Month: date?.toLocaleDateString('en-US', { month: 'long' }) ?? '',
    Mon: date?.toLocaleDateString('en-US', { month: 'short' }) ?? '',
    TwoThousandYear: date ? `Two Thousand ${getTwoDigitsCardinalFromYear(date.getFullYear(), tNumerals)}` : '',
    Hour: tNumerals.cardinals[h12 ?? -1]?.() ?? '',
    Oclock: date ? getOclock(date, tNumerals) : '',
    Timeofday: date ? getTimeOfDay(date, tNumerals) : '',
    Am: date ? (date.getHours() < 12 ? 'am' : 'pm') : '',

    // Location
    Venue,
    Address,
    City,
    CityState,
    CityST
  };

  const YYYY = date?.toLocaleDateString('en-US', { year: 'numeric' }) ?? '';

  const fields: Record<string, string> = {
    // Alphabetics require case variants
    ...mapRecord(Alphabetic, str => toTitleCase(str)),
    ...mapRecord(Alphabetic, str => str.toUpperCase()),
    ...mapRecord(Alphabetic, str => str.toLowerCase()),

    // Numeric
    D: date?.toLocaleDateString('en-US', { day: 'numeric' }) ?? '',
    DD: date?.toLocaleDateString('en-US', { day: '2-digit' }) ?? '',
    M: date?.toLocaleDateString('en-US', { month: 'numeric' }) ?? '',
    MM: date?.toLocaleDateString('en-US', { month: '2-digit' }) ?? '',
    YY: YYYY.slice(-2),
    YYYY,
    h: h12 ? `${h12}` : '',
    m: date?.getMinutes()?.toString() ?? '',
    mm: date?.getMinutes()?.toString().padStart(2, '0') ?? ''
  };

  const layers = page.layers.filter(layer => layer.type === 'text') as TextLayerData[];
  const filledPatterns: string[] = [];
  for (const layer of layers) {
    const pattern = layer.textData.pattern ?? THEME_SYSTEM_LAYER_NAME_TO_PATTERN[layer.name ?? ''];
    if (!pattern) continue;
    const filledPattern = fillPattern(pattern, fields);
    layer.textData.content = filledPattern;
    filledPatterns.push(filledPattern);
  }

  return filledPatterns;
}
